import { inject, Injectable, NgZone } from '@angular/core';
import { EPusherEvents } from '../../core/enums';
import { INotification } from '../../core/interfaces';
import { PUSHER_TOKEN } from '../../core/injection-tokens/pusher-config.token';
import { AuthStore } from '../../store/auth.store';
import { NotificationsStore } from '../../store/notifications.store';
import { PlatformService } from '../platform/platform.service';
declare const Pusher: any;
@Injectable({
  providedIn: 'root'
})
export class PusherApiService {
  pusher: any;
  private authStore = inject(AuthStore);
  private notificationsStore = inject(NotificationsStore);
  private platform = inject(PlatformService);
  private zone = inject(NgZone);
  private lastResumeRefreshAt = 0;
  private readonly resumeRefreshThrottleMs = 1500;
  private notificationsSubscriptionUserId: number | null = null;
  pusherConfigs = inject(PUSHER_TOKEN);
  chatMessagesChannel: any;
  chatMessagesChannelName: string = 'private-chatRoom.';
  chatsListChannel: any;
  chatsListChannelName: string = 'private-chats.';
  notificationsChannel: any;

  constructor() {
    this.createPusherInstance();
  }

  get notificationsChannelName(): string | null {
    const userId = this.authStore.user()?.id;
    return userId ? `private-notifications.${userId}` : null;
  }

  createPusherInstance() {
    if (this.platform.isPlatformBrowser()) {
      this.pusher = new Pusher(this.pusherConfigs.key, {
        cluster: this.pusherConfigs.cluster,
        forceTLS: true,
        channelAuthorization: {
          endpoint: `${this.pusherConfigs.pusherApiUrl}`,
          headersProvider: () => {
            return this.getPusherHeaders();
          },
        }
      });
    }
  }

  refreshConnectionOnResume() {
    if (!this.platform.isPlatformBrowser()) {
      return;
    }

    if (!this.pusher) {
      this.createPusherInstance();
      return;
    }

    const now = Date.now();
    if (now - this.lastResumeRefreshAt < this.resumeRefreshThrottleMs) {
      return;
    }

    this.lastResumeRefreshAt = now;

    const state = this.pusher.connection?.state;
    if (state === 'connecting') {
      return;
    }

    if (state === 'connected') {
      this.pusher.disconnect();
    }

    this.pusher.connect();
  }

  getPusherHeaders() {
    return {
      "Authorization": `Bearer ${this.authStore.accessToken()}`,
    }
  }

  subscribeChatMessagesChannel(chatId: string | number) {
    if (!chatId) {
      return;
    }

    this.chatMessagesChannel = this.pusher?.subscribe(this.chatMessagesChannelName + chatId)
  }
  subscribeChatsListChannel(userId: string | number) {
    if (!userId) {
      return;
    }

    this.chatsListChannel = this.pusher?.subscribe(this.chatsListChannelName + userId)
  }
  ensureNotificationsChannel() {
    const userId = this.authStore.user()?.id;
    const channelName = this.notificationsChannelName;
    if (!userId || !channelName) {
      return;
    }

    if (this.notificationsSubscriptionUserId === userId && this.notificationsChannel) {
      return;
    }

    this.unsubscribeNotificationsChannel();
    this.notificationsChannel = this.pusher?.subscribe(channelName);
    this.notificationsChannel?.bind(EPusherEvents.newNotification, (item: INotification) => {
      this.zone.run(() => this.notificationsStore.pushNewNotification(item));
    });
    this.notificationsSubscriptionUserId = userId;
  }

  unsubscribeNotificationsChannel() {
    const channelName = this.notificationsChannel?.name ?? this.notificationsChannelName;
    this.notificationsChannel?.unbind()
    if (channelName) {
      this.pusher?.unsubscribe(channelName);
    }
    this.notificationsChannel = null;
    this.notificationsSubscriptionUserId = null;
  }
  unsubscribeChatMessagesChannel() {
    const channelName = this.chatMessagesChannel?.name;
    this.chatMessagesChannel?.unbind()
    if (channelName) {
      this.pusher?.unsubscribe(channelName);
    }
    this.chatMessagesChannel = null;
  }
  unsubscribeChatsListChannel() {
    const channelName = this.chatsListChannel?.name;
    this.chatsListChannel?.unbind()
    if (channelName) {
      this.pusher?.unsubscribe(channelName);
    }
    this.chatsListChannel = null;
  }
}
